import type { DrawBetType } from '@nexbit/types'

/**
 * The fixed answers a "choice" bet type can take.
 *
 * A 3top number is anything from 000 to 999, so it is typed. สูง/ต่ำ and คู่/คี่ are not:
 * each has exactly two answers, and a free text box for them asks the operator to remember
 * that the system spells them `high`, `low`, `odd` and `even` — in English, lower case —
 * and to type them without a slip. A list of the two answers removes the question.
 */

export interface BetChoice {
  /** What is stored and sent: the spelling the server validates against. */
  value: string
  /** What the operator reads. */
  label: string
}

const HIGH_LOW: BetChoice[] = [
  { value: 'high', label: 'สูง' },
  { value: 'low', label: 'ต่ำ' },
]
const ODD_EVEN: BetChoice[] = [
  { value: 'even', label: 'คู่' },
  { value: 'odd', label: 'คี่' },
]

/**
 * The choices for a bet type, or null when it is a typed-number type.
 *
 * Null is the signal to keep the text box. It is decided here from the bet type, never by
 * the caller guessing from a digit count, so a new choice type is added in one place.
 */
export function betChoices(betType: DrawBetType): BetChoice[] | null {
  switch (betType) {
    case 'high_low_top':
    case 'high_low_bottom':
      return HIGH_LOW
    case 'odd_even_top':
    case 'odd_even_bottom':
      return ODD_EVEN
    default:
      return null
  }
}

/**
 * The value that stands for "both answers" in a field that accepts a list.
 *
 * The add boxes take several numbers at once ("123 456"), and for a choice type the
 * equivalent is both of them. It is the same space-separated string the text box would
 * have held, so everything downstream — the parser, the validator, the request — sees no
 * difference between a typed "high low" and a chosen ทั้งสอง.
 */
export function bothChoices(choices: BetChoice[]): string {
  return choices.map((c) => c.value).join(' ')
}
